<template>
  <div class="p-6">
    <Card>
      <template #header>
        <div class="p-3">
          <h3 class="text-lg font-semibold m-0">Device Configuration</h3>
          <p class="text-sm text-500 mt-1">
            Configure and manage scales, cameras, and scanners for weight and image capture
          </p>
        </div>
      </template>
      <template #content>
        <!-- Scales Section -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-3">Scales</h4>
          <div v-if="scales.length === 0" class="text-500 italic">
            No scales available. Configure scales in agent settings.
          </div>
          <div v-else class="grid">
            <div v-for="scale in scales" :key="scale.id" class="col-12 md:col-6 lg:col-4">
              <Card class="h-full">
                <template #content>
                  <div class="p-3">
                    <div class="flex justify-content-between align-items-start mb-2">
                      <div>
                        <h5 class="font-semibold m-0">{{ scale.name }}</h5>
                        <div class="text-sm text-500">{{ scale.location || 'No location' }}</div>
                      </div>
                      <Tag
                        :value="getAgentForDevice(scale.id)?.state || 'offline'"
                        :severity="
                          getAgentStatusSeverity(getAgentForDevice(scale.id)?.state || 'offline')
                        "
                      />
                    </div>

                    <div class="text-sm text-500 mb-3">
                      <div>Agent: {{ getAgentForDevice(scale.id)?.name || 'Unknown' }}</div>
                      <div>Driver: {{ scale.driver }}</div>
                    </div>

                    <!-- Camera Assignment -->
                    <div class="mb-3">
                      <label class="text-sm font-medium block mb-1">Assigned Camera</label>
                      <Dropdown
                        v-model="scale.assignedCameraId"
                        :options="getCamerasForAgent(getAgentForDevice(scale.id)?.id)"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Select camera"
                        class="w-full"
                        @change="(e: any) => updateScaleCameraAssignment(scale.id, e.value)"
                      />
                    </div>

                    <!-- Actions -->
                    <div class="flex gap-2">
                      <Button
                        label="Set Default"
                        icon="pi pi-star"
                        size="small"
                        severity="secondary"
                        @click="setDefaultScale(scale.id)"
                        :disabled="defaultScaleId === scale.id"
                      />
                      <Button
                        label="Test Weight"
                        icon="pi pi-refresh"
                        size="small"
                        @click="testScale(scale.id)"
                        :loading="testingDevices.has(scale.id)"
                      />
                    </div>
                  </div>
                </template>
              </Card>
            </div>
          </div>
        </div>

        <!-- Cameras Section -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-3">Cameras</h4>
          <div v-if="cameras.length === 0" class="text-500 italic">
            No cameras available. Configure cameras in agent settings.
          </div>
          <div v-else class="grid">
            <div v-for="camera in cameras" :key="camera.id" class="col-12 md:col-6 lg:col-4">
              <Card class="h-full">
                <template #content>
                  <div class="p-3">
                    <div class="flex justify-content-between align-items-start mb-2">
                      <div>
                        <h5 class="font-semibold m-0">{{ camera.name }}</h5>
                        <div class="text-sm text-500">{{ camera.location || 'No location' }}</div>
                      </div>
                      <Tag
                        :value="getAgentForDevice(camera.id)?.state || 'offline'"
                        :severity="
                          getAgentStatusSeverity(getAgentForDevice(camera.id)?.state || 'offline')
                        "
                      />
                    </div>

                    <div class="text-sm text-500 mb-3">
                      <div>Agent: {{ getAgentForDevice(camera.id)?.name || 'Unknown' }}</div>
                      <div>Modes: {{ camera.modes.join(', ') }}</div>
                    </div>

                    <!-- Actions -->
                    <div class="flex gap-2">
                      <Button
                        label="Set Default"
                        icon="pi pi-star"
                        size="small"
                        severity="secondary"
                        @click="setDefaultCamera(camera.id)"
                        :disabled="defaultCameraId === camera.id"
                      />
                      <Button
                        label="Test Capture"
                        icon="pi pi-camera"
                        size="small"
                        @click="testCamera(camera.id)"
                        :loading="testingDevices.has(camera.id)"
                      />
                    </div>
                  </div>
                </template>
              </Card>
            </div>
          </div>
        </div>

        <!-- Scanners Section -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-3">Scanners</h4>
          <div v-if="scanners.length === 0" class="text-500 italic">
            No scanners available. Configure scanners in agent settings.
          </div>
          <div v-else class="grid">
            <div v-for="scanner in scanners" :key="scanner.id" class="col-12 md:col-6 lg:col-4">
              <Card class="h-full">
                <template #content>
                  <div class="p-3">
                    <div class="flex justify-content-between align-items-start mb-2">
                      <div>
                        <h5 class="font-semibold m-0">{{ scanner.name }}</h5>
                        <div class="text-sm text-500">{{ scanner.location || 'No location' }}</div>
                      </div>
                      <Tag
                        :value="getAgentForDevice(scanner.id)?.state || 'offline'"
                        :severity="
                          getAgentStatusSeverity(getAgentForDevice(scanner.id)?.state || 'offline')
                        "
                      />
                    </div>

                    <div class="text-sm text-500 mb-3">
                      <div>Agent: {{ getAgentForDevice(scanner.id)?.name || 'Unknown' }}</div>
                      <div>Driver: {{ scanner.driver }}</div>
                    </div>

                    <!-- Actions -->
                    <div class="flex gap-2">
                      <Button
                        label="Set Default"
                        icon="pi pi-star"
                        size="small"
                        severity="secondary"
                        @click="setDefaultScanner(scanner.id)"
                        :disabled="defaultScannerId === scanner.id"
                      />
                      <Button
                        label="Test Scan"
                        icon="pi pi-qrcode"
                        size="small"
                        @click="testScanner(scanner.id)"
                        :loading="testingDevices.has(scanner.id)"
                      />
                    </div>
                  </div>
                </template>
              </Card>
            </div>
          </div>
        </div>

        <!-- Debug Info -->
        <div class="mt-4">
          <Accordion>
            <AccordionTab header="Debug Info">
              <div class="grid">
                <div class="col-12 md:col-6">
                  <h4 class="text-sm font-semibold mb-2">Device Summary</h4>
                  <div class="text-xs text-500">
                    <div>Agents: {{ agents.length }}</div>
                    <div>Scales: {{ scales.length }}</div>
                    <div>Cameras: {{ cameras.length }}</div>
                    <div>Scanners: {{ scanners.length }}</div>
                  </div>
                </div>
                <div class="col-12 md:col-6">
                  <h4 class="text-sm font-semibold mb-2">Default Devices</h4>
                  <div class="text-xs text-500">
                    <div>Default Scale: {{ defaultScale?.name || 'None' }}</div>
                    <div>Default Camera: {{ defaultCamera?.name || 'None' }}</div>
                    <div>Default Scanner: {{ defaultScanner?.name || 'None' }}</div>
                  </div>
                </div>
              </div>
            </AccordionTab>
          </Accordion>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import Card from 'primevue/card'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import { useToast } from 'primevue/usetoast'
import { useDevicesStore } from '@/stores/v2/devices'
import { formatDistanceToNow } from 'date-fns'

const toast = useToast()
const devicesStore = useDevicesStore()

// State
const testingDevices = ref(new Set<string>())

// Computed
const agents = computed(() => devicesStore.agents)
const scales = computed(() => devicesStore.scales)
const cameras = computed(() => devicesStore.cameras)
const scanners = computed(() => devicesStore.scanners)
const defaultScale = computed(() => devicesStore.defaultScale)
const defaultCamera = computed(() => devicesStore.defaultCamera)
const defaultScanner = computed(() => devicesStore.defaultScanner)
const defaultScaleId = computed(() => devicesStore.defaultScaleId)
const defaultCameraId = computed(() => devicesStore.defaultCameraId)
const defaultScannerId = computed(() => devicesStore.defaultScannerId)

// Methods
function getAgentForDevice(deviceId: string) {
  return devicesStore.getAgentForDevice(deviceId)
}

function getCamerasForAgent(agentId?: string) {
  if (!agentId) return []
  return cameras.value.filter((camera) => getAgentForDevice(camera.id)?.id === agentId)
}

function setDefaultScale(scaleId: string) {
  devicesStore.setDefaultScale(scaleId)
  toast.add({
    severity: 'success',
    summary: 'Default Scale Set',
    detail: `Set ${scales.value.find((s) => s.id === scaleId)?.name} as default scale`,
    life: 3000,
  })
}

function setDefaultCamera(cameraId: string) {
  devicesStore.setDefaultCamera(cameraId)
  toast.add({
    severity: 'success',
    summary: 'Default Camera Set',
    detail: `Set ${cameras.value.find((c) => c.id === cameraId)?.name} as default camera`,
    life: 3000,
  })
}

function setDefaultScanner(scannerId: string) {
  devicesStore.setDefaultScanner(scannerId)
  toast.add({
    severity: 'success',
    summary: 'Default Scanner Set',
    detail: `Set ${scanners.value.find((s) => s.id === scannerId)?.name} as default scanner`,
    life: 3000,
  })
}

function updateScaleCameraAssignment(scaleId: string, cameraId: string | null) {
  // This would need to be implemented to update the agent config
  // For now, just show a message
  toast.add({
    severity: 'info',
    summary: 'Camera Assignment',
    detail: 'Camera assignment updated. Restart agent to apply changes.',
    life: 5000,
  })
}

async function testScale(scaleId: string) {
  testingDevices.value.add(scaleId)
  try {
    const reading = await devicesStore.readScale(scaleId)
    toast.add({
      severity: 'success',
      summary: 'Scale Test Successful',
      detail: `Weight: ${reading.value} ${reading.unit}`,
      life: 4000,
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Scale Test Failed',
      detail: error instanceof Error ? error.message : 'Unknown error',
      life: 3000,
    })
  } finally {
    testingDevices.value.delete(scaleId)
  }
}

async function testCamera(cameraId: string) {
  testingDevices.value.add(cameraId)
  try {
    const result = await devicesStore.captureCamera(cameraId)
    toast.add({
      severity: 'success',
      summary: 'Camera Test Successful',
      detail: 'Image captured successfully',
      life: 4000,
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Camera Test Failed',
      detail: error instanceof Error ? error.message : 'Unknown error',
      life: 3000,
    })
  } finally {
    testingDevices.value.delete(cameraId)
  }
}

async function testScanner(scannerId: string) {
  testingDevices.value.add(scannerId)
  try {
    const result = await devicesStore.scanId(scannerId)
    toast.add({
      severity: 'success',
      summary: 'Scanner Test Successful',
      detail: `Scanned: ${result.text}`,
      life: 4000,
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Scanner Test Failed',
      detail: error instanceof Error ? error.message : 'Unknown error',
      life: 3000,
    })
  } finally {
    testingDevices.value.delete(scannerId)
  }
}

function getAgentStatusSeverity(state: string) {
  switch (state) {
    case 'online':
      return 'success'
    case 'offline':
      return 'danger'
    default:
      return 'secondary'
  }
}

// Lifecycle
onMounted(() => {
  console.log('DeviceSettings: onMounted')
})

onUnmounted(() => {
  devicesStore.cleanup()
})
</script>
